import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { budgetService } from '../services/budgetService';
import { profileService } from '../services/profileService';
import { Budget, SERVICE_LABELS, ServiceType, getServiceLabel, UserProfile } from '../types';
import { AuthUser } from '../lib/supabase';
import { getLogoBase64 } from '../lib/pdfHelper';
import { formatCurrency, getRoomMetrics, formatArea, formatDim } from '../lib/formatters';

interface DashboardProps {
  user: AuthUser;
}

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'todos' | 'pendente' | 'aprovado' | 'cancelado'>('todos');
  const [monthFilter, setMonthFilter] = useState(() => new Date().toISOString().slice(0, 7));
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    loadBudgets();
  }, [user.id]);

  const loadBudgets = async () => {
    setLoading(true);
    const [budgetsData, profileData] = await Promise.all([
      budgetService.getBudgets(user.id),
      profileService.getProfile(user.id)
    ]);
    setBudgets(budgetsData);
    setProfile(profileData);
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Excluir permanentemente este registro?')) {
      const success = await budgetService.deleteBudget(id);
      if (success) {
        loadBudgets();
      } else {
        alert('Erro ao excluir orçamento.');
      }
    }
  };

  const handleEdit = (budget: Budget) => {
    navigate('/', { state: { budget } });
  };

  const handleStatusChange = async (id: string, status: 'pendente' | 'aprovado' | 'cancelado') => {
    const success = await budgetService.updateBudgetStatus(id, status);
    if (success) {
      loadBudgets();
    }
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const exportBudgetToPDF = async (budget: Budget) => {
    const profile = await profileService.getProfile(user.id);
    const { jsPDF } = (window as any).jspdf;
    const doc = new jsPDF();
    const logoBase64 = await getLogoBase64();

    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', 14, 10, 20, 20);
    }

    doc.setFontSize(22);
    doc.setTextColor(37, 99, 235);
    doc.text('Gesso Milenio', 105, 20, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(profile?.company_name || 'Minha Empresa', 105, 27, { align: 'center' });

    doc.setDrawColor(200);
    doc.setFontSize(18);
    doc.setTextColor(30, 41, 59);
    doc.text(`Orçamento Detalhado`, 14, 40);

    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text(`Cliente: ${budget.client_name}`, 14, 50);
    doc.text(`Contato: ${budget.client_phone || 'N/A'}`, 14, 57);
    doc.text(`Data: ${formatDate(budget.created_at)}`, 14, 64);

    // Legacy support: if no rooms, use budget dimensions
    if (!budget.rooms || budget.rooms.length === 0) {
      doc.text(`Medidas: ${budget.width || 0}m x ${budget.depth || 0}m (Altura: ${budget.height || 0}m)`, 14, 71);
    }
    doc.text(`Data: ${new Date(budget.created_at).toLocaleDateString('pt-BR')}`, 14, 64);

    let currentY = 80;

    // Group items by room
    const rooms = budget.rooms && budget.rooms.length > 0 ? budget.rooms : [{ id: 'default', name: 'Geral', width: budget.width, depth: budget.depth, height: budget.height }];

    rooms.forEach((room) => {
      const roomItems = budget.items.filter(i => i.room_id === room.id || (!i.room_id && room.id === 'default'));
      if (roomItems.length === 0) return;

      if (currentY > 250) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(30, 41, 59);
      const roomDims = room.width ? `${room.width}m x ${room.depth}m` : '';
      doc.text(`${room.name} ${roomDims}`, 14, currentY);
      currentY += 6;

      const metrics = getRoomMetrics(room as any);
      if (metrics.area > 0) {
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(`Total: ${formatArea(metrics.area)}`, 14, currentY);
        currentY += 4;
      }

      const tableRows = roomItems.map(item => {
        const isManual = !!item.manual_name;
        const label = item.manual_name || getServiceLabel(item.service_type, profile);
        const unit = label.includes('(m²)') ? 'm²' : label.includes('(m)') ? 'm' : 'un';
        return [
          label.split(' (')[0],
          isManual ? 'Item Avulso' : `${item.quantity}${unit}`,
          isManual ? '-' : formatCurrency(item.unit_price),
          formatCurrency(item.subtotal)
        ];
      });

      const roomTotal = roomItems.reduce((acc, item) => acc + item.subtotal, 0);

      (doc as any).autoTable({
        startY: currentY,
        head: [['Serviço', 'Quantidade', 'P. Unitário', 'Subtotal']],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [30, 41, 59] },
        foot: [['', '', 'Subtotal', formatCurrency(roomTotal)]],
        footStyles: { fillColor: [100, 116, 139], textColor: [255, 255, 255], fontStyle: 'bold' },
        margin: { left: 14, right: 14 }
      });

      currentY = (doc as any).lastAutoTable.finalY + 15;
    });

    // Grand Total
    const finalY = currentY;
    doc.setFillColor(37, 99, 235);
    doc.rect(14, finalY, 180, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`TOTAL GERAL: ${formatCurrency(budget.total_value)}`, 105, finalY + 10, { align: 'center' });


    if (budget.notes) {
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.setFont('helvetica', 'normal');
      doc.text(`Observações: ${budget.notes}`, 14, finalY + 25, { maxWidth: 180 });
    }

    doc.setFontSize(9);
    doc.setTextColor(150);
    doc.text('Documento gerado digitalmente por Gesso Milenio.', 105, doc.internal.pageSize.height - 15, { align: 'center' });

    // Summary of materials at the end if there are rooms (meaning items are split)
    if (budget.rooms && budget.rooms.length > 0) {
      doc.addPage();
      doc.setFontSize(18);
      doc.setTextColor(30, 41, 59);
      doc.text('Resumo por Material', 14, 20);

      // Metragem por cômodo + total
      const metricsRows = budget.rooms.map(r => {
        const m = getRoomMetrics(r as any);
        return [r.name, `${formatDim(r.width)} × ${formatDim(r.depth)} m`, formatArea(m.area)];
      });
      const totalAreaPdf = budget.rooms.reduce((acc, r) => acc + getRoomMetrics(r as any).area, 0);

      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.text('Metragem por Cômodo', 14, 30);

      (doc as any).autoTable({
        startY: 34,
        head: [['Cômodo', 'Dimensões', 'Total']],
        body: metricsRows,
        theme: 'striped',
        headStyles: { fillColor: [30, 41, 59] },
        foot: [['', 'METRAGEM TOTAL', formatArea(totalAreaPdf)]],
        footStyles: { fillColor: [0, 91, 174], textColor: [255, 255, 255], fontStyle: 'bold' },
        margin: { left: 14, right: 14 }
      });

      const materialTotals = budget.items.reduce((acc, item) => {
        const type = item.service_type;
        const key = item.manual_name ? `manual_${item.manual_name}` : type;
        if (!acc[key]) acc[key] = {
          quantity: 0,
          subtotal: 0,
          label: item.manual_name || getServiceLabel(type, profile),
          isManual: !!item.manual_name
        };
        acc[key].quantity += item.quantity;
        acc[key].subtotal += item.subtotal;
        return acc;
      }, {} as Record<string, { quantity: number; subtotal: number; label: string; isManual: boolean }>);

      const summaryRows = (Object.entries(materialTotals) as [string, { quantity: number; subtotal: number; label: string; isManual: boolean }][]).map(([_, data]) => {
        const unit = data.label.includes('(m²)') ? 'm²' : data.label.includes('(m)') ? 'm' : 'un';
        return [
          data.label.split(' (')[0],
          data.isManual ? 'Item Avulso' : `${data.quantity.toFixed(2)}${unit}`,
          formatCurrency(data.subtotal)
        ];
      });

      const matStartY = ((doc as any).lastAutoTable?.finalY || 30) + 12;
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.text('Total por Material', 14, matStartY - 4);

      (doc as any).autoTable({
        startY: matStartY,
        head: [['Material', 'Metragem Total', 'Valor Total']],
        body: summaryRows,
        theme: 'grid',
        headStyles: { fillColor: [30, 41, 59] },
        foot: [['', 'TOTAL GERAL', formatCurrency(budget.total_value)]],
        footStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: 'bold' },
        margin: { left: 14, right: 14 }
      });
    }

    const fileName = `Orcamento_GM_${budget.client_name.replace(/\s+/g, '_')}.pdf`;

    try {
      const blob = doc.output('blob');
      const file = new File([blob], fileName, { type: 'application/pdf' });

      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile && navigator.share && (navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Orçamento GM',
          text: `Orçamento para ${budget.client_name}`
        });
      } else {
        doc.save(fileName);
      }
    } catch (error) {
      console.error('Error sharing PDF:', error);
      doc.save(fileName);
    }
  };

  const filteredBudgets = budgets.filter(b => {
    const matchesSearch = b.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'todos' || b.status === filterStatus;

    let matchesDate = true;

    if (monthFilter) {
      const budgetDate = new Date(b.created_at);
      const [year, month] = monthFilter.split('-').map(Number);
      if (budgetDate.getFullYear() !== year || (budgetDate.getMonth() + 1) !== month) {
        matchesDate = false;
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const stats = {
    totalValue: filteredBudgets.reduce((acc, b) => acc + (b.status === 'aprovado' ? b.total_value : 0), 0),
    quotedValue: filteredBudgets.reduce((acc, b) => acc + b.total_value, 0),
    totalCount: filteredBudgets.length,
    approvedCount: filteredBudgets.filter(b => b.status === 'aprovado').length,
    conversionRate: filteredBudgets.length > 0 ? (filteredBudgets.filter(b => b.status === 'aprovado').length / filteredBudgets.length) * 100 : 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-blue-700 to-blue-900 p-6 rounded-2xl shadow-lg shadow-blue-900/20 border border-blue-600/30 flex flex-col justify-between text-center">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Faturado no Período</span>
          <h4 className="text-lg font-black text-emerald-600 mt-2 truncate">{formatCurrency(stats.totalValue)}</h4>
          <p className="text-[10px] text-slate-400 mt-1">Orçamentos aprovados</p>
        </div>
        <div className="bg-gradient-to-br from-blue-700 to-blue-900 p-6 rounded-2xl shadow-lg shadow-blue-900/20 border border-blue-600/30 flex flex-col justify-between text-center">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Ticket Médio</span>
          <h4 className="text-lg font-black text-white mt-2 truncate">{formatCurrency(stats.totalCount > 0 ? stats.quotedValue / stats.totalCount : 0)}</h4>
          <p className="text-[10px] text-slate-400 mt-1">Valor médio das propostas</p>
        </div>
        <div className="bg-gradient-to-br from-blue-700 to-blue-900 p-6 rounded-2xl shadow-lg shadow-blue-900/20 border border-blue-600/30 flex flex-col justify-between text-center">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Conversão</span>
          <h4 className="text-lg font-black text-blue-600 mt-2 truncate">{stats.conversionRate.toFixed(1)}%</h4>
          <p className="text-[10px] text-slate-400 mt-1">{stats.approvedCount} de {stats.totalCount} fechados</p>
        </div>
        <div className="bg-gradient-to-br from-blue-800 to-indigo-900 p-6 rounded-2xl shadow-lg shadow-blue-900/30 border border-blue-600/30 flex flex-col justify-between text-white text-center">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Volume Orçado</span>
          <h4 className="text-lg font-black text-white mt-2 truncate">{formatCurrency(stats.quotedValue)}</h4>
          <p className="text-[10px] text-slate-400 mt-1">Valor total de propostas</p>
        </div>
      </div>

      <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-3xl shadow-lg shadow-blue-900/20 border border-blue-600/30 overflow-hidden">
        <div className="p-4 md:p-5 border-b border-blue-600/30 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Histórico & Métricas</h2>
            <button
              onClick={() => { setMonthFilter(''); setSearchTerm(''); setFilterStatus('todos'); }}
              className="text-xs font-bold text-blue-300 hover:text-red-400 transition-colors flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-blue-800/50"
            >
              <i className="fa-solid fa-filter-circle-xmark"></i> Limpar
            </button>
          </div>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-4 relative group">
              <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors"></i>
              <input
                type="text"
                placeholder="Pesquisar cliente..."
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="col-span-12 md:col-span-3">
              <div className="relative">
                <i className="fa-solid fa-filter absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                <select
                  className="w-full pl-11 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-blue-500 outline-none appearance-none transition-all text-slate-600"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as any)}
                >
                  <option value="todos">Todos os Status</option>
                  <option value="pendente">Pendentes</option>
                  <option value="aprovado">Aprovados</option>
                  <option value="cancelado">Cancelados</option>
                </select>
                <i className="fa-solid fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none"></i>
              </div>
            </div>

            <div className="col-span-12 md:col-span-3 md:col-start-10">
              <div className="relative">
                <i className="fa-regular fa-calendar absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                <input
                  type="month"
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all text-slate-600 appearance-none"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Cliente</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Data</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Total</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                <th className="px-6 py-4 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredBudgets.map((budget) => (
                <tr key={budget.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-bold text-slate-800 block">{budget.client_name}</span>
                    <span className="text-xs text-slate-400">{budget.client_phone || 'Sem contato'}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap">{formatDate(budget.created_at)}</td>
                  <td className="px-6 py-4 text-right">
                    <span className="font-black text-slate-900 whitespace-nowrap">{formatCurrency(budget.total_value)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <select className={`text-[10px] font-black uppercase px-3 py-1.5 rounded-full outline-none border-none transition-all ${budget.status === 'aprovado' ? 'bg-emerald-100 text-emerald-700' : budget.status === 'cancelado' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`} value={budget.status} onChange={(e) => handleStatusChange(budget.id, e.target.value as any)}>
                      <option value="pendente">Pendente</option>
                      <option value="aprovado">Aprovado</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => exportBudgetToPDF(budget)} className="text-slate-400 hover:text-slate-800 transition-colors p-2 rounded-lg hover:bg-slate-100" title="Exportar PDF">
                        <i className="fa-solid fa-file-pdf"></i>
                      </button>
                      <button onClick={() => handleEdit(budget)} className="text-blue-400 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50" title="Editar">
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                      <button onClick={() => handleDelete(budget.id)} className="text-slate-300 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-50" title="Excluir">
                        <i className="fa-solid fa-trash-can"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredBudgets.length === 0 && <div className="p-12 text-center text-slate-300"><i className="fa-solid fa-inbox text-4xl mb-2"></i><p>Nenhum registro encontrado para este período.</p></div>}
        </div>

        {/* Mobile List View */}
        <div className="md:hidden space-y-4">
          {filteredBudgets.map((budget) => (
            <div key={budget.id} className="p-5 bg-blue-800/50 rounded-3xl shadow-sm border border-blue-600/30 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4">
                <select
                  className={`px-2 py-1 rounded-full text-[10px] font-black uppercase outline-none border-none transition-all appearance-none ${budget.status === 'aprovado' ? 'bg-emerald-100 text-emerald-700' : budget.status === 'cancelado' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}
                  value={budget.status}
                  onChange={(e) => handleStatusChange(budget.id, e.target.value as any)}
                >
                  <option value="pendente">PENDENTE</option>
                  <option value="aprovado">APROVADO</option>
                  <option value="cancelado">CANCELADO</option>
                </select>
              </div>
              <div className="pr-16 mb-4">
                <h3 className="font-bold text-white text-lg leading-tight">{budget.client_name}</h3>
                <div className="text-xs text-blue-200 font-medium mt-1 flex items-center gap-1">
                  <i className="fa-regular fa-calendar"></i> {formatDate(budget.created_at)}
                </div>
              </div>
              <div className="flex justify-between items-end">
                <div>
                  <span className="text-[10px] font-bold text-blue-200 uppercase tracking-widest block mb-0.5">Total</span>
                  <div className="text-2xl font-black text-white tracking-tight">{formatCurrency(budget.total_value)}</div>
                </div>
                <button
                  onClick={() => setExpandedId(expandedId === budget.id ? null : budget.id)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${expandedId === budget.id ? 'bg-slate-100 text-slate-600' : 'bg-blue-600 text-white'}`}
                >
                  {expandedId === budget.id ? 'Fechar' : 'Ver Detalhes'}
                  <i className={`fa-solid fa-chevron-${expandedId === budget.id ? 'up' : 'down'}`}></i>
                </button>
              </div>
              {expandedId === budget.id && (
                <div className="mt-5 pt-5 border-t border-slate-50 animate-fade-in space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 p-3 rounded-2xl">
                      <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Contato</span>
                      <a href={`https://wa.me/55${budget.client_phone?.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="font-bold text-emerald-600 text-sm flex items-center gap-1">
                        <i className="fa-brands fa-whatsapp"></i> {budget.client_phone || 'N/A'}
                      </a>
                    </div>
                    {(!budget.rooms || budget.rooms.length === 0) && (
                      <div className="bg-slate-50 p-3 rounded-2xl">
                        <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Dimensões</span>
                        <div className="font-bold text-slate-700 text-sm">{budget.width}m x {budget.depth}m</div>
                      </div>
                    )}
                  </div>

                  {/* Detalhes dos Cômodos e Itens */}
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block mb-2 px-1">Detalhamento por Cômodo</span>

                    {(budget.rooms && budget.rooms.length > 0) ? (
                      <div className="space-y-4">
                        {budget.rooms.map((room, rIdx) => {
                          const roomItems = budget.items.filter(i => i.room_id === room.id);
                          if (roomItems.length === 0) return null;
                          const m = getRoomMetrics(room);
                          return (
                            <div key={rIdx} className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-bold text-slate-700 text-sm">{room.name}</span>
                                <span className="text-[10px] font-semibold text-slate-500">{formatDim(room.width)} × {formatDim(room.depth)} m</span>
                              </div>
                              {m.area > 0 && (
                                <div className="flex justify-end mb-3 pb-3 border-b border-slate-200/70">
                                  <span className="text-[10px] font-black uppercase tracking-wider text-blue-700">Total: {formatArea(m.area)}</span>
                                </div>
                              )}
                              <div className="space-y-2">
                                {roomItems.map((item, idx) => (
                                  <div key={idx} className="flex justify-between items-center text-xs">
                                    <div className="flex flex-col">
                                      {(() => {
                                        const isManual = !!item.manual_name;
                                        const label = item.manual_name || getServiceLabel(item.service_type, profile);
                                        const unit = label.includes('(m²)') ? ' m²' : label.includes('(m)') ? ' m' : ' un';
                                        return (
                                          <>
                                            <span className="text-slate-600 font-medium">{label.split(' (')[0]}</span>
                                            {!isManual && (
                                              <span className="text-[10px] text-slate-400">
                                                {item.quantity.toFixed(2)}{unit}
                                              </span>
                                            )}
                                          </>
                                        )
                                      })()}
                                    </div>
                                    <span className="font-bold text-slate-900">{formatCurrency(item.subtotal)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        })}

                        {/* Metragem Total Consolidada */}
                        {(() => {
                          const totalArea = budget.rooms!.reduce((acc, r) => acc + getRoomMetrics(r).area, 0);
                          if (totalArea === 0) return null;
                          return (
                            <div className="mt-4 flex justify-between items-center rounded-2xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.12)' }}>
                              <span className="text-[11px] font-black uppercase tracking-widest text-blue-200">
                                <i className="fa-solid fa-ruler-combined mr-1.5"></i>Metragem Total
                              </span>
                              <span className="text-base font-black text-white">{formatArea(totalArea)}</span>
                            </div>
                          );
                        })()}

                        {/* Resumo Total por Material */}
                        <div className="mt-6 pt-4 border-t-2 border-dashed border-white/20">
                          <span className="text-[10px] font-black text-blue-200 uppercase tracking-widest block mb-4 px-1 text-center">Resumo Consolidado de Materiais</span>
                          <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.10)' }}>
                            <div className="space-y-3">
                              {Object.entries(
                                budget.items.reduce((acc, item) => {
                                  const type = item.service_type;
                                  const key = item.manual_name ? `manual_${item.manual_name}` : type;
                                  if (!acc[key]) acc[key] = {
                                    quantity: 0,
                                    subtotal: 0,
                                    label: item.manual_name || getServiceLabel(type, profile),
                                    isManual: !!item.manual_name
                                  };
                                  acc[key].quantity += item.quantity;
                                  acc[key].subtotal += item.subtotal;
                                  return acc;
                                }, {} as Record<string, { quantity: number; subtotal: number; label: string; isManual: boolean }>)
                              ).map(([key, data], idx) => {
                                const typedData = data as { quantity: number; subtotal: number; label: string; isManual: boolean };
                                const unit = typedData.label.includes('(m²)') ? ' m²' : typedData.label.includes('(m)') ? ' m' : ' un';
                                return (
                                  <div key={idx} className="flex justify-between items-center border-b border-white/10 pb-2 last:border-0 last:pb-0">
                                    <div>
                                      <span className="text-white text-xs font-bold block">{typedData.label.split(' (')[0]}</span>
                                      <span className="text-[10px] text-blue-300 font-black uppercase">
                                        {typedData.isManual ? 'ITEM AVULSO' : `${typedData.quantity.toFixed(2)}${unit} (TOTAL)`}
                                      </span>
                                    </div>
                                    <span className="text-white font-black text-sm">{formatCurrency(typedData.subtotal)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Fallback para orçamentos antigos
                      <div className="space-y-2">
                        {budget.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-xs p-3 border border-slate-100 rounded-xl">
                            <div className="flex flex-col">
                              <span className="text-slate-600 font-medium">{SERVICE_LABELS[item.service_type as ServiceType]?.split(' (')[0]}</span>
                              <span className="text-[10px] text-slate-400">
                                {item.quantity.toFixed(2)}{SERVICE_LABELS[item.service_type as ServiceType].includes('(m²)') ? ' m²' : ' m'}
                              </span>
                            </div>
                            <span className="font-bold text-slate-900">{formatCurrency(item.subtotal)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <button onClick={() => exportBudgetToPDF(budget)} className="py-3 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold flex items-center justify-center gap-2 hover:bg-slate-200 border border-slate-200">
                      <i className="fa-solid fa-file-pdf"></i> PDF
                    </button>
                    <button onClick={() => handleEdit(budget)} className="py-3 bg-blue-50 text-blue-600 rounded-xl text-xs font-bold flex items-center justify-center gap-2 hover:bg-blue-100">
                      <i className="fa-solid fa-pen"></i> Editar
                    </button>
                    <button onClick={() => handleDelete(budget.id)} className="col-span-2 py-3 bg-red-50 text-red-600 rounded-xl text-xs font-bold flex items-center justify-center gap-2 hover:bg-red-100">
                      <i className="fa-solid fa-trash"></i> Excluir Orçamento
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {filteredBudgets.length === 0 && <div className="p-12 text-center text-slate-300 bg-white rounded-3xl border border-slate-100"><i className="fa-solid fa-inbox text-4xl mb-2"></i><p>Nenhum registro encontrado.</p></div>}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
